/**
 * System Dashboard Page
 *
 * Comprehensive visualization of all system databases and AMLKG status
 */
// React 18 with jsx runtime doesn't need React import
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  ServerIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CubeIcon,
  ClockIcon,
  SignalIcon,
  SignalSlashIcon,
  BoltIcon,
  TagIcon,
} from '@heroicons/react/24/outline'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import { apiClient } from '@/services/api'
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts'

const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4']

const DATABASE_INFO: Record<string, { name: string; description: string; icon: string }> = {
  postgresql: { name: 'PostgreSQL', description: 'Primary relational database', icon: '🐘' },
  redis: { name: 'Redis', description: 'Cache & message queue', icon: '🔴' },
  // The engine-backed knowledge layer surfaces here under the legacy "memgraph"
  // and "weaviate" keys (the /system/status response keeps the names for
  // back-compat). Both now point at the NeuroGraph Rust engine — Memgraph and
  // Weaviate themselves were retired 2026-04-30.
  memgraph: { name: 'NeuroGraph (Graph)', description: 'Engine graph layer (RocksDB)', icon: '🧠' },
  weaviate: { name: 'NeuroGraph (Vector)', description: 'Engine vector layer (inline HNSW)', icon: '🔍' },
}

interface DatabaseCardProps {
  name: string
  status: string
  latency?: number
  host?: string
  error?: string
  details?: any
  classes?: number
  collections?: number
}

function DatabaseCard({ name, status, latency, host, error, details: _details, classes, collections }: DatabaseCardProps) {
  const info = DATABASE_INFO[name] || { name, description: 'Database', icon: '💾' }

  const statusIcon = status === 'healthy' ? (
    <CheckCircleIcon className="w-6 h-6 text-green-500" />
  ) : status === 'degraded' ? (
    <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500" />
  ) : (
    <XCircleIcon className="w-6 h-6 text-red-500" />
  )

  const statusBadge = status === 'healthy' ? 'success' : status === 'degraded' ? 'warning' : 'error'

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{info.icon}</span>
          <div>
            <h3 className="font-semibold text-foreground">{info.name}</h3>
            <p className="text-sm text-muted-foreground">{info.description}</p>
          </div>
        </div>
        {statusIcon}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Status</span>
          <Badge variant={statusBadge as any}>{status}</Badge>
        </div>

        {latency !== undefined && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Latency</span>
            <span className="text-sm font-medium">{latency.toFixed(1)} ms</span>
          </div>
        )}

        {host && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Host</span>
            <span className="text-sm font-mono text-foreground">{host}</span>
          </div>
        )}

        {classes !== undefined && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Classes</span>
            <span className="text-sm font-medium">{classes}</span>
          </div>
        )}

        {collections !== undefined && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Collections</span>
            <span className="text-sm font-medium">{collections}</span>
          </div>
        )}

        {error && (
          <div className="mt-2 p-2 bg-red-500/10 rounded text-xs text-red-600 break-all">
            {error}
          </div>
        )}
      </div>
    </Card>
  )
}

// AMLKGLayersCard, MemgraphCard, WeaviateCard were removed at the engine-only
// cutover (2026-05-08). The NeuralGraph cards below cover graph + vector state.
// ── NeuralGraph cards (Phase 2 of dashboard cutover) ─────────────────────────

function NeuralGraphHealthCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['neuralgraph-health'],
    queryFn: () => apiClient.getNeuralGraphHealth(),
    refetchInterval: 30000,
  })

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <div className="h-32 bg-muted rounded"></div>
      </Card>
    )
  }

  const reachable = data?.reachable
  const drift = data?.drift_status
  const driftBadge = drift === 'healthy' ? 'success' : drift === 'drifting' ? 'warning' : 'gray'
  const last = data?.last_extraction_at
  const lastRel = last ? relativeTime(last) : '—'
  const eps = data?.drift_episodes
  const ents = data?.drift_entities

  return (
    <Card>
      <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
        <BoltIcon className="w-5 h-5 text-blue-500" />
        NeuralGraph
      </h3>

      <div className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground">Reachable</span>
          {reachable ? (
            <span className="flex items-center gap-1 text-emerald-600 font-medium">
              <SignalIcon className="w-4 h-4" /> live
            </span>
          ) : (
            <span className="flex items-center gap-1 text-red-600 font-medium">
              <SignalSlashIcon className="w-4 h-4" /> down
            </span>
          )}
        </div>

        {reachable && (
          <>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Latency</span>
              <span className="font-mono text-foreground">{data?.latency_ms ?? '—'} ms</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">UES enabled</span>
              <Badge variant={data?.ues_enabled ? 'success' : 'gray'}>
                {data?.ues_enabled ? 'yes' : 'no'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">API key</span>
              <Badge variant={data?.api_key_configured ? 'success' : 'warning'}>
                {data?.api_key_configured ? 'configured' : 'missing'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Drift</span>
              <Badge variant={driftBadge as any}>{drift || 'unknown'}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Last UES extract</span>
              <span className="text-foreground" title={last || ''}>{lastRel}</span>
            </div>
          </>
        )}

        {!reachable && data?.error && (
          <div className="text-xs text-red-500 mt-2 break-words">{data.error}</div>
        )}
      </div>

      {(eps || ents) && (
        <div className="mt-3 pt-3 border-t border-border space-y-1.5 text-xs">
          {eps && (
            <div className="flex items-center justify-between text-muted-foreground">
              <span>Episodes</span>
              <span>
                {/* drift API still uses in_memgraph/in_weaviate field names for
                    back-compat; both come from the engine now */}
                {eps.in_memgraph} graph · {eps.in_weaviate} vector
                {eps.missing_layer > 0 && (
                  <span className="text-amber-500 ml-2">(missing layer: {eps.missing_layer})</span>
                )}
              </span>
            </div>
          )}
          {ents && (
            <div className="flex items-center justify-between text-muted-foreground">
              <span>Entities</span>
              <span>
                {ents.with_layer}/{ents.in_memgraph} aligned
                {ents.sales_playbook_tagged != null && (
                  <span className="ml-2 inline-flex items-center gap-0.5">
                    <TagIcon className="w-3 h-3" />{ents.sales_playbook_tagged} sales-playbook
                  </span>
                )}
              </span>
            </div>
          )}
          {drift === 'drifting' && data?.drift_reasons && data.drift_reasons.length > 0 && (
            <div className="text-amber-600 mt-1">
              <ExclamationTriangleIcon className="w-3 h-3 inline mr-1" />
              {data.drift_reasons.join('; ')}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

const NG_LAYER_ORDER = ['l1_episodic', 'l2_semantic', 'l3_procedural', 'l4_conceptual', 'l5_meta', 'l6_instrumental']
const NG_LAYER_LABEL: Record<string, string> = {
  l1_episodic: 'Episodic',
  l2_semantic: 'Semantic',
  l3_procedural: 'Procedural',
  l4_conceptual: 'Conceptual',
  l5_meta: 'MetaMemory',
  l6_instrumental: 'Instrumental',
}
const NG_LAYER_TTL: Record<string, string> = {
  l1_episodic: '14d',
  l2_semantic: '365d',
  l3_procedural: '90d',
  l4_conceptual: '365d',
  l5_meta: '7y',
  l6_instrumental: '365d',
}

function NeuralGraphLayerChart() {
  const { data, isLoading } = useQuery({
    queryKey: ['neuralgraph-summary'],
    queryFn: () => apiClient.getNeuralGraphSummary(),
    refetchInterval: 60000,
  })

  if (isLoading) {
    return (
      <Card className="animate-pulse lg:col-span-2">
        <div className="h-64 bg-muted rounded"></div>
      </Card>
    )
  }

  // Always include all 6 layers so the chart shape is stable even with zero counts
  const finalData = NG_LAYER_ORDER.map(key => {
    const info = data?.layers?.[key]
    return {
      name: NG_LAYER_LABEL[key] || key,
      ttl: NG_LAYER_TTL[key] || '',
      count: info?.count || 0,
      edges: info?.edge_count || 0,
    }
  })

  return (
    <Card className="lg:col-span-2">
      <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
        <CubeIcon className="w-5 h-5 text-purple-500" />
        NeuralGraph 6-Layer Memory
        <span className="ml-auto text-xs text-muted-foreground font-normal">
          {data?.total_nodes?.toLocaleString() || 0} total nodes
        </span>
      </h3>

      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={finalData} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border, #e5e7eb)" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={90} />
            <Tooltip
              contentStyle={{
                background: 'var(--color-card, #fff)',
                border: '1px solid var(--color-border, #e5e7eb)',
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(value: number, _name: string, props: any) => [
                `${value.toLocaleString()} nodes (TTL ${props.payload.ttl})`,
                'Count',
              ]}
            />
            <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]}>
              {finalData.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        {finalData.map((d, i) => (
          <span
            key={d.name}
            className="text-[10px] px-2 py-0.5 rounded-full border"
            style={{ borderColor: COLORS[i % COLORS.length] + '60', color: COLORS[i % COLORS.length] }}
          >
            {d.name} · TTL {d.ttl}
          </span>
        ))}
      </div>
    </Card>
  )
}

function UESActivityStrip() {
  const { data, isLoading } = useQuery({
    queryKey: ['ues-activity-24h'],
    queryFn: () => apiClient.getUESActivity(24),
    refetchInterval: 60000,
  })

  if (isLoading) {
    return (
      <Card className="animate-pulse">
        <div className="h-24 bg-muted rounded"></div>
      </Card>
    )
  }

  const series = (data?.bins || []).map(b => ({
    ts: new Date(b.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    extractions: b.extractions,
  }))

  return (
    <Card>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
          <BoltIcon className="w-4 h-4 text-amber-500" />
          UES Activity (24h)
        </h3>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>{data?.total_extractions || 0} extractions</span>
          <span>avg conf {(data?.avg_confidence || 0).toFixed(2)}</span>
          <span className={data && data.degraded_pct > 5 ? 'text-amber-500' : ''}>
            {(data?.degraded_pct || 0).toFixed(1)}% degraded
          </span>
        </div>
      </div>

      {series.length > 0 ? (
        <div className="h-20">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={series} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="uesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="ts" tick={false} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip
                contentStyle={{
                  background: 'var(--color-card, #fff)',
                  border: '1px solid var(--color-border, #e5e7eb)',
                  borderRadius: 8,
                  fontSize: 11,
                }}
              />
              <Area
                type="monotone"
                dataKey="extractions"
                stroke="#f59e0b"
                strokeWidth={2}
                fill="url(#uesGrad)"
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-20 flex items-center justify-center text-xs text-muted-foreground">
          No UES extractions in the last 24 hours
        </div>
      )}
    </Card>
  )
}

// ── PII Proxy Health card (Phase 2 of pii simulation plan) ───────────────────

function PIIProxyHealthCard() {
  const { data: summary } = useQuery({
    queryKey: ['pii-metrics-summary'],
    queryFn: () => apiClient.getPIIMetricsSummary(),
    refetchInterval: 60_000,
    retry: false,
  })
  const { data: density } = useQuery({
    queryKey: ['pii-density-24h'],
    queryFn: () => apiClient.getPIIDensity(24),
    refetchInterval: 60_000,
    retry: false,
  })

  const edgeMutation = useMutation({
    mutationFn: () => apiClient.runPIIEdgeCases(),
  })

  const scan = summary?.scan as any
  const cats = summary?.category_distribution_24h as any
  const canary = summary?.canary as any

  const series = (density?.bins || []).map(b => ({
    ts: new Date(b.ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    detections: b.detections,
  }))

  const categoryBars = cats
    ? Object.entries(cats).map(([name, count]) => ({ name, count: count as number })).sort((a, b) => b.count - a.count).slice(0, 8)
    : []

  const edgeScore = edgeMutation.data
    ? `${edgeMutation.data.passed}/${edgeMutation.data.total}`
    : '—'

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
          <BoltIcon className="w-4 h-4 text-emerald-500" />
          PII Proxy Health
          {scan?.pattern_version && (
            <Badge variant="gray">v{scan.pattern_version}</Badge>
          )}
        </h3>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => edgeMutation.mutate()}
          disabled={edgeMutation.isPending}
        >
          <ArrowPathIcon className={'w-4 h-4 mr-1 ' + (edgeMutation.isPending ? 'animate-spin' : '')} />
          {edgeMutation.isPending ? 'Running…' : 'Run edge cases'}
        </Button>
      </div>

      {/* Tile row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
        <div className="bg-muted/40 rounded p-2">
          <p className="text-muted-foreground text-[10px] uppercase tracking-wide">Status</p>
          <p className="text-foreground font-semibold mt-0.5 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>LIVE
          </p>
        </div>
        <div className="bg-muted/40 rounded p-2">
          <p className="text-muted-foreground text-[10px] uppercase tracking-wide">Detections 24h</p>
          <p className="text-foreground font-semibold mt-0.5 tabular-nums">
            {density?.total_detections?.toLocaleString() || '0'}
          </p>
        </div>
        <div className="bg-muted/40 rounded p-2">
          <p className="text-muted-foreground text-[10px] uppercase tracking-wide">Edge cases</p>
          <p className="text-foreground font-semibold mt-0.5 tabular-nums">
            {edgeScore}
            {edgeMutation.data && edgeMutation.data.failed > 0 && (
              <span className="text-red-500 text-[10px] ml-1">{edgeMutation.data.failed} failed</span>
            )}
          </p>
        </div>
        <div className="bg-muted/40 rounded p-2">
          <p className="text-muted-foreground text-[10px] uppercase tracking-wide">Active sessions</p>
          <p className="text-foreground font-semibold mt-0.5 tabular-nums">
            {(summary?.sessions as any)?.active_now ?? 0}
          </p>
        </div>
        <div className="bg-muted/40 rounded p-2">
          <p className="text-muted-foreground text-[10px] uppercase tracking-wide">Canary</p>
          <p className="text-foreground font-semibold mt-0.5">
            {canary?.success_rate != null
              ? `${(canary.success_rate * 100).toFixed(1)}%`
              : '—'}
          </p>
        </div>
      </div>

      {/* Sparkline + category breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Detections / hour (24h)</p>
          {series.length > 0 ? (
            <div className="h-24">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="piiGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="ts" tick={false} axisLine={false} tickLine={false} />
                  <YAxis hide />
                  <Tooltip
                    contentStyle={{ background: 'var(--color-card, #fff)', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8, fontSize: 11 }}
                  />
                  <Area type="monotone" dataKey="detections" stroke="#10b981" strokeWidth={2} fill="url(#piiGrad)" isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-24 flex items-center justify-center text-xs text-muted-foreground bg-muted/20 rounded">
              No detections yet — run /pii-proxy/test or the Simulation tab to populate.
            </div>
          )}
        </div>

        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Top categories</p>
          {categoryBars.length > 0 ? (
            <div className="h-24">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={categoryBars} layout="vertical" margin={{ top: 2, right: 8, left: 60, bottom: 2 }}>
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" width={70} tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-24 flex items-center justify-center text-xs text-muted-foreground bg-muted/20 rounded">
              No categories emitted yet
            </div>
          )}
        </div>
      </div>

      {/* Edge-case failures (if any) */}
      {edgeMutation.data && edgeMutation.data.failures.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Edge case failures ({edgeMutation.data.failures.length})
          </p>
          <ul className="space-y-0.5 max-h-32 overflow-y-auto text-xs">
            {edgeMutation.data.failures.slice(0, 10).map(f => (
              <li key={f.id} className="flex items-center gap-2 py-0.5">
                <span className="text-red-500 font-mono text-[10px]">✗</span>
                <span className="text-foreground font-mono text-[10px]">{f.id}</span>
                <span className="text-muted-foreground text-[10px] truncate">
                  expected {f.expected}, got {f.actual}
                </span>
              </li>
            ))}
            {edgeMutation.data.failures.length > 10 && (
              <li className="text-[10px] text-muted-foreground text-center pt-1">
                +{edgeMutation.data.failures.length - 10} more
              </li>
            )}
          </ul>
        </div>
      )}
    </Card>
  )
}

function relativeTime(iso: string): string {
  try {
    const ms = Date.now() - new Date(iso).getTime()
    if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
    return `${Math.floor(ms / 86_400_000)}d ago`
  } catch { return iso }
}

export default function SystemDashboardPage() {
  // Fetch system status
  const { data: systemStatus, isLoading: loadingSystem, refetch: refetchSystem } = useQuery({
    queryKey: ['system-status'],
    queryFn: () => apiClient.getSystemStatus(),
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  // Memgraph/Weaviate/AMLKG summary queries were removed at the engine-only
  // cutover (2026-05-08). The NeuralGraph health + layer chart at the top of
  // the page now covers the graph + vector state.

  const overallStatus = systemStatus?.status || 'unknown'
  const statusColor = overallStatus === 'healthy' ? 'text-green-600' :
                      overallStatus === 'degraded' ? 'text-yellow-600' : 'text-red-600'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
            <ServerIcon className="w-7 h-7 text-primary" />
            System Dashboard
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Monitor all databases and knowledge graph systems
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Status:</span>
            <span className={`font-semibold capitalize ${statusColor}`}>{overallStatus}</span>
          </div>
          <Button variant="secondary" size="sm" onClick={() => refetchSystem()}>
            <ArrowPathIcon className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Card */}
      {systemStatus && (
        <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold opacity-90">System Health Summary</h2>
              <p className="text-3xl font-bold mt-2">
                {systemStatus.summary.healthy} / {systemStatus.summary.total}
              </p>
              <p className="text-sm opacity-80 mt-1">databases healthy</p>
            </div>
            <div className="text-right">
              <ClockIcon className="w-8 h-8 opacity-80 mb-2" />
              <p className="text-sm opacity-80">
                Last updated: {new Date(systemStatus.timestamp).toLocaleTimeString()}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* NeuralGraph row (Phase 2 of cutover) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <NeuralGraphHealthCard />
        <NeuralGraphLayerChart />
      </div>

      {/* UES activity sparkline */}
      <UESActivityStrip />

      {/* PII Proxy Health (Phase 2 of pii simulation plan) */}
      <PIIProxyHealthCard />

      {/* Database Status Grid */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">Database Status</h2>
        {loadingSystem ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="h-32 bg-muted rounded"></div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(systemStatus?.databases || {}).map(([name, data]) => (
              <DatabaseCard
                key={name}
                name={name}
                status={data.status}
                latency={data.latency_ms}
                host={data.host}
                error={data.error}
                details={data.details}
                classes={data.classes}
                collections={data.collections}
              />
            ))}
          </div>
        )}
      </div>

      {/* Memgraph + Weaviate detail cards were removed at the engine-only
          cutover (2026-05-08). The NeuralGraph Health card and 6-Layer chart
          at the top of this page already cover graph + vector state, which
          all flows through the NeuroGraph engine now. */}

      {/* Quick Links */}
      <Card>
        <h3 className="text-lg font-semibold text-foreground mb-4">External Dashboards</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <a
            href="https://agents.ziro.pub/docs"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-green-500/10 dark:bg-green-950/20 rounded-lg hover:bg-green-500/15 dark:hover:bg-green-950/30 transition-colors"
          >
            <span className="text-xl">📚</span>
            <span className="font-medium text-green-600 dark:text-green-400">API Docs</span>
          </a>
          <a
            href="https://planner.ziro.pub"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-blue-500/10 dark:bg-blue-950/20 rounded-lg hover:bg-blue-500/15 dark:hover:bg-blue-950/30 transition-colors"
          >
            <span className="text-xl">📋</span>
            <span className="font-medium text-blue-600 dark:text-blue-400">Planner</span>
          </a>
          <a
            href="https://docs.ziro.pub"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-3 bg-purple-500/10 dark:bg-purple-950/20 rounded-lg hover:bg-purple-500/15 dark:hover:bg-purple-950/30 transition-colors"
          >
            <span className="text-xl">📝</span>
            <span className="font-medium text-purple-600 dark:text-purple-400">WYSIWYG Editor</span>
          </a>
        </div>
      </Card>
    </div>
  )
}
